import { QURAN_CREDIT, WORDS_CREDIT, kemenagUrl, qurancomUrl } from '@iqrapath/shared'
import { SourceLink } from '../../components/SourceLink.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { Bookmark, BookmarkCheck, BookOpenText, ChevronDown, ChevronLeft, ChevronRight, Flag, Pause, Play, Type } from 'lucide-react'
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AudioButton } from '../../components/AudioButton.tsx'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Sheet } from '../../components/Sheet.tsx'
import { SkVerses } from '../../components/Skeleton.tsx'
import { ApiError } from '../../lib/api.ts'
import { clearAudioError, stopAudio, toggleAudio, useAudio } from '../../lib/audio.ts'
import { AR_SIZES, setArSize, setLatinOn, useArSize, useLatinOn } from '../../lib/prefs.ts'
import { sparkBurst } from '../../lib/motion.ts'
import { scrollToEl } from '../../lib/scroll.ts'
import { useToast } from '../../lib/toast.ts'
import { arNum, type Verse } from '../ayat/ayat.ts'
import { Terjemah } from '../ayat/Iqra.tsx'
import { WordSheet, type WordItem } from '../ayat/WordSheet.tsx'
import { PAGE, fetchSurahAudio, pageStart, pushRecent, setReadingMark, toggleTanda, useReading, useSurahPages, useSurahs, useTanda, type SurahInfo } from './quran.ts'

/** Height of the sticky reader bar: a verse counts as "on screen" once its bottom passes below it. */
const BAR = 72

function ViewSheet({ onClose }: { onClose: () => void }) {
  const size = useArSize()
  const latin = useLatinOn()
  return (
    <Sheet title="Tampilan" onClose={onClose}>
      <p className="help" style={{ marginTop: 4 }}>Ukuran huruf Arab</p>
      <div className="seg" role="group" aria-label="Ukuran huruf Arab" style={{ marginTop: 8 }}>
        {AR_SIZES.map((s, i) => (
          <button key={s} type="button" aria-pressed={size === i} onClick={() => setArSize(i)} aria-label={`Ukuran ${i + 1}`}>
            <span className="ar" lang="ar" aria-hidden="true" style={{ fontSize: `${0.9 + i * 0.22}rem`, lineHeight: 1.4 }}>ب</span>
          </button>
        ))}
      </div>
      <div className="set-row" style={{ padding: '16px 0 4px' }}>
        <b id="r-lat" style={{ flex: 1 }}>Tulisan latin</b>
        <button type="button" role="switch" aria-checked={latin} aria-labelledby="r-lat" className="switch" onClick={() => setLatinOn(!latin)} />
      </div>
    </Sheet>
  )
}

/** "Pindah ayat": a verse number, quick ranges, and the surahs before and after. */
function JumpSheet({ s, prev, next, onGo, onClose }: { s: SurahInfo; prev?: SurahInfo; next?: SurahInfo; onGo: (k: number) => void; onClose: () => void }) {
  const [val, setVal] = useState('')
  const k = Number(val)
  const ok = val !== '' && Number.isInteger(k) && k >= 1 && k <= s.jumlah
  const step = s.jumlah > 120 ? PAGE : 20
  const ranges = s.jumlah > step ? Array.from({ length: Math.ceil(s.jumlah / step) }, (_, i) => [i * step + 1, Math.min(s.jumlah, (i + 1) * step)] as const) : []
  const submit = (e: FormEvent) => { e.preventDefault(); if (ok) onGo(k) }
  return (
    <Sheet title="Pindah ayat" onClose={onClose}>
      <form onSubmit={submit}>
        <label className="field" style={{ marginTop: 4 }}>
          <span>Nomor ayat di {s.nama} (1–{s.jumlah})</span>
          <input className="input num" inputMode="numeric" pattern="[0-9]*" autoComplete="off" value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, '').slice(0, 3))} />
        </label>
        <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} disabled={!ok}>{ok ? `Buka ayat ${k}` : 'Buka ayat'}</button>
      </form>
      {ranges.length > 0 && (
        <div className="jump-ranges" role="group" aria-label="Lompat cepat">
          {ranges.map(([a, b]) => <button key={a} type="button" className="chip-btn num" onClick={() => onGo(a)}>{a}–{b}</button>)}
        </div>
      )}
      {(prev || next) && (
        <div className="jump-nav">
          {prev ? <Link className="btn btn-ghost btn-sm" to={`/quran/${prev.n}`}><ChevronLeft size={16} aria-hidden="true" />{prev.nama}</Link> : <span />}
          {next && <Link className="btn btn-ghost btn-sm" to={`/quran/${next.n}`}>{next.nama}<ChevronRight size={16} aria-hidden="true" /></Link>}
        </div>
      )}
    </Sheet>
  )
}

function ReaderVerse({ v, n, latin, playing, flash, saved, onTanda, isLast, onLast }: {
  v: Verse
  n: number
  latin: boolean
  playing: boolean
  flash: boolean
  saved: boolean
  onTanda: (el: HTMLElement) => void
  /** this verse carries the "terakhir baca" marker */
  isLast: boolean
  onLast: (el: HTMLElement) => void
}) {
  const [words, setWords] = useState(false)
  const [open, setOpen] = useState<number | null>(null)
  const items: WordItem[] = (v.kata ?? []).map((w, i, all) => ({ k: v.k, i, n: all.length, w, id: `${n}:${v.k}:${i}`, isKey: false }))
  return (
    <article className={`card verse r-verse${playing ? ' playing' : ''}${flash ? ' flash' : ''}${isLast ? ' is-last' : ''}`} id={`a-${v.k}`} data-k={v.k} aria-label={`Ayat ${v.k}`}>
      {isLast && <p className="last-ribbon"><Flag size={14} aria-hidden="true" /> Terakhir dibaca di sini</p>}
      <div className="verse-head">
        <span className="pill pill-mint num">{n}:{v.k}</span>
        <div className="v-actions">
          <button type="button" className="chip-btn tanda-btn last-btn" aria-pressed={isLast} aria-label={`Tandai terakhir baca di ayat ${v.k}`} onClick={(e) => onLast(e.currentTarget)}>
            <Flag size={17} aria-hidden="true" />
          </button>
          <button type="button" className="chip-btn tanda-btn" aria-pressed={saved} aria-label={`Tandai ayat ${v.k}`} onClick={(e) => onTanda(e.currentTarget)}>
            {saved ? <BookmarkCheck size={18} aria-hidden="true" /> : <Bookmark size={18} aria-hidden="true" />}
          </button>
          {items.length > 0 && (
            <button type="button" className="chip-btn" aria-pressed={words} onClick={() => setWords(!words)}>Per kata</button>
          )}
          {v.audio && <AudioButton id={`q-${n}-${v.k}`} srcs={[v.audio]} what={`ayat ${v.k}`} compact />}
        </div>
      </div>
      <p className="ar verse-ar" lang="ar" dir="rtl">{v.ar} <span className="end">۝{arNum(v.k)}</span></p>
      {words && (
        <div className="words" dir="rtl" aria-label={`Kata-kata ayat ${v.k}`} role="group">
          {items.map((it, i) => (
            <button key={it.id} type="button" className="word" onClick={() => setOpen(i)}>
              <span className="ar" lang="ar">{it.w.ar}</span>
              <small dir="ltr">{it.w.arti ?? '…'}</small>
            </button>
          ))}
        </div>
      )}
      {latin && v.latin && <p className="lat">{v.latin}</p>}
      <p className="tr"><Terjemah text={v.terjemah} /></p>
      {v.footnotes && <p className="fn">{v.footnotes}</p>}
      {v.wajiz && (
        <details className="wajiz">
          <summary>Tafsir Ringkas</summary>
          <p className="prose">{v.wajiz}</p>
          <p className="credit"><SourceLink href={kemenagUrl(n, v.k)}>Tafsir Ringkas Kemenag, QS {n}:{v.k}</SourceLink></p>
        </details>
      )}
      {open !== null && <WordSheet surah={n} items={items} at={open} onMove={setOpen} onClose={() => setOpen(null)} />}
    </article>
  )
}

function Reader({ s, start, jump }: { s: SurahInfo; start: number; jump: number | null }) {
  const n = s.n
  const qc = useQueryClient()
  const q = useSurahPages(n, start)
  const audio = useAudio()
  const latin = useLatinOn()
  const size = useArSize()
  const { data: surahs } = useSurahs()
  const { data: tanda } = useTanda()
  const { data: reading } = useReading()
  const navigate = useNavigate()
  const [toast, show] = useToast()
  const [view, setView] = useState(false)
  const [jumpOpen, setJumpOpen] = useState(false)
  const [flash, setFlash] = useState<number | null>(null)
  const [current, setCurrent] = useState<number | null>(null)
  const sentinel = useRef<HTMLDivElement>(null)
  const jumped = useRef(false)

  const pages = q.data?.pages ?? []
  const verses = pages.flatMap((p) => p.verses)
  const first = pages[0]
  // "Putar surat" clip id carries its first verse so the playing verse can be highlighted: surah-<n>-<from>
  const clip = audio.id?.startsWith(`surah-${n}-`) ? Number(audio.id.split('-')[2]) : null
  const playingK = clip !== null ? clip + audio.pos : null

  useEffect(() => stopAudio, [])
  useEffect(() => pushRecent(n), [n])
  useEffect(() => {
    if (!audio.failed) return
    const t = setTimeout(clearAudioError, 2600)
    return () => clearTimeout(t)
  }, [audio.failed])

  // The query result is a new object every render: effects read it through a ref so they run only on real changes.
  const qr = useRef(q)
  qr.current = q
  const loadMore = () => { const c = qr.current; if (c.hasNextPage && !c.isFetchingNextPage) void c.fetchNextPage() }

  // ?ayat=N: scroll to it once it is loaded, and mark it briefly.
  const loaded = verses.length
  useEffect(() => {
    if (!jump || jumped.current || !document.getElementById(`a-${jump}`)) return
    jumped.current = true
    const go = () => { const el = document.getElementById(`a-${jump}`); if (el) scrollToEl(el, { smooth: false }) }
    go()
    // the Arabic font may still be loading: the verses above grow when it arrives, so align once more
    if (document.fonts && document.fonts.status !== 'loaded') void document.fonts.ready.then(go)
    setFlash(jump)
  }, [jump, loaded])
  useEffect(() => {
    if (flash === null) return
    const t = setTimeout(() => setFlash(null), 2400)
    return () => clearTimeout(t)
  }, [flash])

  // Follow the surah playback: keep the playing verse in view, load the next page before the queue reaches it.
  useEffect(() => {
    if (playingK === null || !audio.playing) return
    const el = document.getElementById(`a-${playingK}`)
    if (el) scrollToEl(el, { block: 'center' })
    else loadMore()
  }, [playingK, audio.playing, loaded])

  // The verse at the top (first one whose bottom is below the sticky bar): "ayat X / Y" in the bar. Only shown, never
  // saved: "Lanjut baca" moves only with "Tandai terakhir baca".
  useEffect(() => {
    let raf = 0
    const measure = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        for (const el of document.querySelectorAll<HTMLElement>('.r-verse')) {
          if (el.getBoundingClientRect().bottom > BAR + 8) { setCurrent(Number(el.dataset.k)); return }
        }
      })
    }
    window.addEventListener('scroll', measure, { passive: true })
    measure()
    return () => { window.removeEventListener('scroll', measure); cancelAnimationFrame(raf) }
  }, [loaded])

  // Load the next page when the end of the list comes near.
  useEffect(() => {
    const el = sentinel.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver((e) => { if (e[0]?.isIntersecting) loadMore() }, { rootMargin: '600px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const playSurah = async () => {
    const from = first?.from ?? 1
    const id = `surah-${n}-${from}`
    if (audio.id === id) return toggleAudio(id, [])
    const { audio: list } = await qc.fetchQuery({ queryKey: ['surah-audio', n], queryFn: () => fetchSurahAudio(n), staleTime: Infinity })
    toggleAudio(id, list.slice(from - 1))
  }
  const surahOn = clip !== null && audio.playing
  const nextSurah = surahs?.find((x) => x.n === n + 1)
  const prevSurah = surahs?.find((x) => x.n === n - 1)
  const shown = current ?? first?.from ?? 1
  const marked = new Set((tanda ?? []).filter((t) => t.surah === n).map((t) => t.ayah))
  const lastK = reading?.surah === n ? reading.ayah : null

  /** "Tandai terakhir baca": one marker for the whole Qur'an; on the marked verse again it is removed. */
  const onLast = async (k: number, el: HTMLElement) => {
    const on = lastK !== k
    if (on) sparkBurst(el, 8)
    const err = await setReadingMark(qc, on ? { surah: n, ayah: k } : null)
    if (err) show(err instanceof ApiError && err.status !== 0 ? err.message : 'Belum bisa disimpan. Cek koneksi, lalu coba lagi.')
    else show(on ? `Terakhir baca: ${s.nama} ayat ${k}. Lanjutkan dari sini lewat “Lanjut baca”.` : 'Penanda terakhir baca dihapus.')
  }

  const onTanda = async (k: number, el: HTMLElement) => {
    const on = !marked.has(k)
    if (on) sparkBurst(el, 8)
    const err = await toggleTanda(qc, { surah: n, ayah: k, nama: s.nama }, on)
    if (err) show(err instanceof ApiError && err.status !== 0 ? err.message : 'Belum bisa disimpan. Cek koneksi, lalu coba lagi.')
    else show(on ? `Ayat ${k} ditandai. Lihat di tab Tanda.` : `Tanda ayat ${k} dihapus.`)
  }
  // An already loaded verse is scrolled to; otherwise the reader opens again at that verse (?ayat=N).
  const goTo = (k: number) => {
    setJumpOpen(false)
    const el = document.getElementById(`a-${k}`)
    if (el) { scrollToEl(el); setFlash(k) }
    else navigate(`/quran/${n}?ayat=${k}`, { replace: true })
  }

  return (
    <div className="reader" style={{ '--ar': AR_SIZES[size] } as CSSProperties}>
      <div className="reader-bar">
        <Link className="icon-btn plain" to="/quran" aria-label="Daftar surat"><ChevronLeft size={22} /></Link>
        <button type="button" className="rb-t" onClick={() => setJumpOpen(true)} aria-label={`${s.nama}, ayat ${shown} dari ${s.jumlah}. Pindah ayat`}>
          <b>{s.nama}</b>
          <small className="num">ayat {shown} / {s.jumlah} <ChevronDown size={12} aria-hidden="true" /></small>
        </button>
        <button type="button" className="icon-btn plain" aria-label="Pengaturan tampilan" onClick={() => setView(true)}><Type size={20} /></button>
        <span className="rb-progress" aria-hidden="true"><i style={{ width: `${Math.round((shown / s.jumlah) * 100)}%` }} /></span>
      </div>

      <section className="card surah-banner" aria-label={`Surat ${s.nama}`}>
        <p className="ar" lang="ar">{s.namaAr}</p>
        <h1>{s.nama}</h1>
        <p className="muted">{s.arti} · {s.tempat} · {s.jumlah} ayat</p>
        {verses.some((v) => v.audio) && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={playSurah} aria-label={surahOn ? 'Jeda bacaan surat' : 'Putar surat'}>
            {surahOn ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
            {surahOn ? 'Jeda' : first && first.from > 1 ? `Putar dari ayat ${first.from}` : 'Putar surat'}
          </button>
        )}
      </section>

      {q.isPending && <SkVerses label="Memuat ayat" />}
      {q.isError && (
        <EmptyState Icon={BookOpenText} title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => q.refetch()}>Coba lagi</button>}>
          {q.error instanceof ApiError && q.error.status !== 0 ? q.error.message : 'Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.'}
        </EmptyState>
      )}

      {q.hasPreviousPage && (
        <button type="button" className="btn btn-ghost btn-block load-more" onClick={() => q.fetchPreviousPage()} disabled={q.isFetchingPreviousPage}>
          {q.isFetchingPreviousPage ? 'Memuat…' : `Muat ayat sebelumnya (${Math.max(1, first!.from - 40)}–${first!.from - 1})`}
        </button>
      )}

      {first?.bismillah && (
        <div className="bismillah">
          <p className="ar" lang="ar" dir="rtl">{first.bismillah.ar}</p>
          <p className="muted">{first.bismillah.terjemah}</p>
        </div>
      )}

      <div className="stack r-list">
        {verses.map((v) => (
          <ReaderVerse
            key={v.k} v={v} n={n} latin={latin} playing={playingK === v.k && audio.playing} flash={flash === v.k}
            saved={marked.has(v.k)} onTanda={(el) => void onTanda(v.k, el)}
            isLast={lastK === v.k} onLast={(el) => void onLast(v.k, el)}
          />
        ))}
      </div>

      <div ref={sentinel} />
      {q.hasNextPage && (
        <button type="button" className="btn btn-ghost btn-block load-more" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          {q.isFetchingNextPage ? 'Memuat…' : 'Muat ayat berikutnya'}
        </button>
      )}
      {q.data && !q.hasNextPage && (
        <div className="surah-end">
          <p className="muted">Akhir Surat {s.nama}</p>
          {nextSurah && (
            <Link className="card row-card" to={`/quran/${nextSurah.n}`}>
              <span className="ic" aria-hidden="true"><BookOpenText size={20} /></span>
              <div><small>Surat berikutnya</small><b>{nextSurah.nama}</b></div>
              <ChevronRight className="chev" size={18} aria-hidden="true" />
            </Link>
          )}
        </div>
      )}
      {verses.length > 0 && (
        <p className="credit" style={{ textAlign: 'center', marginTop: 16 }}>
          <SourceLink href={kemenagUrl(s.n, 1, s.jumlah)}>{QURAN_CREDIT}</SourceLink> · <SourceLink href={qurancomUrl(s.n, 1, s.jumlah)}>{WORDS_CREDIT}</SourceLink>
        </p>
      )}

      <div className="toast-slot" role="status">
        {audio.failed ? <p className="toast">Suara belum bisa diputar. Cek koneksi, lalu coba lagi.</p> : toast && <p className="toast">{toast}</p>}
      </div>
      {view && <ViewSheet onClose={() => setView(false)} />}
      {jumpOpen && <JumpSheet s={s} prev={prevSurah} next={nextSurah} onGo={goTo} onClose={() => setJumpOpen(false)} />}
    </div>
  )
}

/** /quran/:surah[?ayat=N] — reader (docs/PRD.md US-5.2–5.4). */
export function SurahPage() {
  const n = Number(useParams().surah)
  const [params] = useSearchParams()
  const { data: surahs, isPending, isError, refetch } = useSurahs()
  if (!Number.isInteger(n) || n < 1 || n > 114) return <Navigate to="/quran" replace />
  if (isPending) return <div className="page"><SkVerses label="Memuat surat" /></div>
  if (isError) {
    return (
      <div className="page">
        <EmptyState Icon={BookOpenText} title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      </div>
    )
  }
  const s = surahs.find((x) => x.n === n)
  if (!s) {
    return (
      <div className="page">
        <EmptyState Icon={BookOpenText} title="Surat belum tersedia" action={<Link className="btn btn-ghost" to="/quran">Daftar surat</Link>}>
          Data surat ini belum terpasang di server.
        </EmptyState>
      </div>
    )
  }
  const want = Number(params.get('ayat'))
  const jump = Number.isInteger(want) && want >= 1 ? Math.min(want, s.jumlah) : null
  return <Reader key={`${n}-${jump ?? 1}`} s={s} start={pageStart(jump ?? 1)} jump={jump} />
}
